import React, { useEffect, useState } from "react";
import { TopBar } from "../components/TopBar";
import { api, formatMoney, formatDate } from "../lib/api";
import { ScrollText, Check, AlertTriangle, Mail, CalendarPlus, BellPlus, CircleDollarSign, TrendingUp, Activity } from "lucide-react";

const Tile = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="bg-ledger-surface border border-ledger-border p-6 md:p-7">
    <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-ledger-muted">
      <span>{label}</span>
      <Icon className="h-4 w-4" style={{ color: accent }} />
    </div>
    <div className="mt-3 font-display text-4xl md:text-5xl text-ledger-ink">{value}</div>
    {sub && <div className="mt-1 font-mono text-[11px] text-ledger-sub">{sub}</div>}
  </div>
);

export default function Impact({ setPasteOpen }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.getImpact().then(setData);
  }, []);

  const fulfilRate = data && data.captured > 0
    ? Math.round((data.fulfilled / data.captured) * 100)
    : 0;

  return (
    <div className="min-h-screen paper-grain" data-testid="impact-page">
      <TopBar onOpenPaste={() => setPasteOpen(true)} />

      <main className="mx-auto max-w-[1700px] px-6 md:px-12 py-10 md:py-14">
        <div className="mb-10 animate-fadeUp">
          <div className="text-[11px] uppercase tracking-[0.22em] text-ledger-muted">
            Impact · calculated from actual activity
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl text-ledger-ink">
            Is Commitment Ledger actually preventing promises from disappearing?
          </h1>
          <p className="mt-3 text-ledger-sub max-w-3xl">
            Every metric on this page comes from real events stored in the app.
            Nothing is fabricated.
          </p>
        </div>

        {!data ? (
          <div className="h-40 bg-ledger-surface border border-ledger-border grid place-items-center font-mono text-sm text-ledger-muted">
            Calculating…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-fadeUp" style={{ animationDelay: "80ms" }}>
              <Tile icon={ScrollText} label="Commitments captured" value={data.captured} accent="#171717" testid="impact-captured" />
              <Tile icon={Check} label="Fulfilled" value={data.fulfilled} sub={`${fulfilRate}% of captured`} accent="#047857" testid="impact-fulfilled" />
              <Tile icon={AlertTriangle} label="Overdue surfaced" value={data.overdue_surfaced} sub="would have been missed" accent="#B91C1C" testid="impact-overdue" />
              <Tile icon={Mail} label="Follow-ups drafted" value={data.follow_ups_drafted} accent="#1D4ED8" testid="impact-followups" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 animate-fadeUp" style={{ animationDelay: "120ms" }}>
              <Tile icon={CalendarPlus} label="Demo calendar events" value={data.calendar_events_created} accent="#171717" testid="impact-calendar" />
              <Tile icon={BellPlus} label="Reminders set" value={data.reminders_set} accent="#171717" testid="impact-reminders" />
              <Tile icon={CircleDollarSign} label="Money still owed to you" value={formatMoney(data.money_open || 0)} accent="#B45309" testid="impact-money-open" />
              <Tile icon={TrendingUp} label="Money marked recovered" value={formatMoney(data.money_recovered || 0)} accent="#047857" testid="impact-money-recovered" />
            </div>

            <section
              className="mt-10 bg-ledger-surface border border-ledger-border animate-fadeUp"
              style={{ animationDelay: "160ms" }}
              data-testid="impact-recent"
            >
              <div className="px-6 py-4 border-b border-ledger-border flex items-center gap-2">
                <Activity className="h-4 w-4 text-ledger-sub" />
                <div className="text-[11px] uppercase tracking-[0.18em] text-ledger-muted">
                  Recent impact
                </div>
              </div>
              {data.recent_events?.length > 0 ? (
                <ul className="divide-y divide-ledger-border">
                  {data.recent_events.map((e, i) => (
                    <li
                      key={i}
                      data-testid={`impact-event-${i}`}
                      className="px-6 py-4 flex items-start justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <div className="font-sans text-sm text-ledger-ink">{e.head}</div>
                        <div className="text-[12px] text-ledger-sub truncate">{e.detail}</div>
                      </div>
                      <div className="font-mono text-[11px] text-ledger-muted shrink-0">
                        {formatDate(e.at)}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-6 py-10 text-center text-sm text-ledger-muted font-mono">
                  Activity will appear here as you use the app.
                </div>
              )}
            </section>

            <div className="mt-14 bg-ledger-surface border border-ledger-border p-8 animate-fadeUp" style={{ animationDelay: "200ms" }}>
              <div className="text-[11px] uppercase tracking-[0.22em] text-ledger-muted">
                Product philosophy
              </div>
              <div className="mt-2 font-display text-2xl text-ledger-ink max-w-3xl leading-snug">
                People make commitments in conversations. Commitment Ledger makes sure those
                commitments don&rsquo;t disappear.
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
